import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@monksflow/monks-ui';
import { ImageOffIcon } from 'lucide-react';

export interface CreativeThumbnailProps {
  /** The platform's rendered preview, absent whenever the platform exposes none. */
  previewUrl: string | null | undefined;
  /** The creative's name, used as the image's alternative text and the dialog's title. */
  label: string;
}

/**
 * A creative's preview, enlarged on click.
 *
 * The placeholder is a first-class state, not an error: most search ads have no image to
 * show and several platforms report delivery per creative without exposing the asset, so
 * a creative with no preview is ordinary and must still occupy the column. A failed load
 * collapses to the same placeholder rather than a broken-image glyph - the preview URLs
 * come from the platforms and expire on their own schedule.
 */
export function CreativeThumbnail({ previewUrl, label }: CreativeThumbnailProps) {
  const { t } = useTranslation('projects');
  const [failed, setFailed] = useState(false);
  const canPreview = Boolean(previewUrl) && !failed;

  if (!canPreview) {
    return (
      <span
        className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted"
        title={t('performanceDashboard.creativeThumbnail.noPreviewAvailable')}
        data-testid="creative-thumbnail-placeholder"
      >
        <ImageOffIcon className="size-4 text-muted-foreground" aria-hidden />
      </span>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="size-10 shrink-0 overflow-hidden rounded-md border border-border-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('performanceDashboard.creativeThumbnail.viewFullSize', { label })}
          data-testid="creative-thumbnail"
        >
          <img
            src={previewUrl ?? undefined}
            alt=""
            className="size-full object-cover"
            loading="lazy"
            data-testid="creative-thumbnail-image"
            onError={() => setFailed(true)}
          />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <img
            src={previewUrl ?? undefined}
            alt={label}
            className="max-h-[70vh] w-full object-contain"
            onError={() => setFailed(true)}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
